/* Right-hand properties panel. */
import { DOOR_TYPES, OPENING_DEFS } from './types.ts';
import type { AngleConstraint, Constraint, OpeningType, Wall } from './types.ts';
import { $, constraintsOf, findItem, hooks, pushUndo, state } from './state.ts';
import { clamp, fmtArea, fmtLen, getUnits, IN_PER_CM, parseLen, plain, uid } from './utils.ts';
import { constraintDist, distToSeg, wallAngleDeg, wallClear, wallVec } from './geom.ts';
import { clampOpenings, shiftWallEnd } from './weld.ts';
import { setWallThickness, solveConstraints } from './solver.ts';
import { FIXTURES, FIXTURE_CATEGORIES, FIXTURE_KW } from './fixtures.ts';
import { deleteSel, duplicateSel, flipDoor, rotateSel } from './ops.ts';
import { CONFLICT_MSG, removeConstraint } from './edit.ts';
import { palette, requestDraw, CSS_W, CSS_H } from './render.ts';
import { TOOLS } from './tools.ts';
import { addRoom } from './ops.ts';
import { detectRect, rectFromClear, resizeRect, roomInside, type RoomSel } from './room.ts';
import { beginPaletteDrag } from './paldrag.ts';

/** Room tool "type a size" form — inside size, remembered across renders, per unit system. */
const roomSizes = { ft: { w: 144, h: 120 }, m: { w: 400 * IN_PER_CM, h: 300 * IN_PER_CM } };

export function renderPanel(): void {
  const panel = $('#panel');
  panel.innerHTML = '';
  if (state.tool === 'fixture') { renderPalette(panel); return; }
  if (state.tool === 'room') { renderRoomPanel(panel); return; }
  if (state.tool === 'constrain') { renderConstrain(panel); requestDraw(); return; }
  if (DOOR_TYPES.includes(state.tool as OpeningType)) { renderDoorTypes(panel); return; }
  if (!state.sel.size) { renderTips(panel); requestDraw(); return; }
  if (state.sel.size > 1) {
    if (state.sel.size === 4) {
      const rr4 = [...state.sel].map(findItem);
      if (rr4.every(x => x && x.kind === 'walls')) {
        const room = detectRect(rr4.map(x => x!.item as Wall));
        if (room) { renderRoomSel(panel, room); requestDraw(); return; }
      }
    }
    panel.appendChild(h3(`${state.sel.size} items`));
    if (state.sel.size === 2) {
      const rr2 = [...state.sel].map(findItem);
      if (rr2.every(x => x && x.kind === 'walls')) {
        renderPairSection(panel, rr2[0]!.item as Wall, rr2[1]!.item as Wall);
      }
    }
    panel.appendChild(btnRow([['Delete', deleteSel, 'danger'], ['Duplicate', duplicateSel]]));
    requestDraw(); return;
  }
  const r = findItem([...state.sel][0]);
  if (!r) { renderTips(panel); return; }
  const it = r.item;
  if (r.kind === 'walls') {
    panel.appendChild(h3('Wall'));
    const v2 = wallVec(it);
    const lenRow = propLen('Length', v2.len, nv => {
      if (nv > 0 && !it.locked) {
        pushUndo();
        if (!shiftWallEnd(state.doc, it, v2.ux * (nv - v2.len), v2.uy * (nv - v2.len))) hooks.notify(CONFLICT_MSG);
        clampOpenings(state.doc); hooks.markDirty();
      }
    });
    const lk = document.createElement('button');
    lk.className = 'lockbtn' + (it.locked ? ' on' : '');
    lk.title = it.locked ? 'Unlock length' : 'Lock length — drags and edits can’t change it';
    lk.textContent = it.locked ? '🔒' : '🔓';
    lk.addEventListener('click', () => {
      pushUndo();
      if (it.locked) delete it.locked; else it.locked = true;
      hooks.markDirty(); renderPanel();
    });
    lenRow.appendChild(lk);
    if (it.locked) (lenRow.querySelector('input') as HTMLInputElement).disabled = true;
    panel.appendChild(lenRow);
    const clr = wallClear(state.doc.walls, it);
    if (v2.len - clr > .3 && clr > 0) {
      // editable: set the interior face-to-face span; the room resizes to match
      const eaten = v2.len - clr;
      const clrRow = propLen('Clear inside', clr, nv => {
        if (nv > 0 && !it.locked) {
          pushUndo();
          if (!shiftWallEnd(state.doc, it, v2.ux * (nv + eaten - v2.len), v2.uy * (nv + eaten - v2.len)))
            hooks.notify(CONFLICT_MSG);
          clampOpenings(state.doc); hooks.markDirty();
        }
      });
      if (it.locked) (clrRow.querySelector('input') as HTMLInputElement).disabled = true;
      panel.appendChild(clrRow);
    }
    for (const c of constraintsOf(it.id)) {
      const row = constraintRow(c, it);
      if (row) panel.appendChild(row);
    }
    // axis constraint: pin this wall horizontal / vertical
    if (!constraintsOf(it.id).some(c => c.type === 'axis')) {
      panel.appendChild(btnRow([
        ['— Horizontal', () => addConstraint({ id: uid('c'), type: 'axis', a: it.id, axis: 'h' }, it.id)],
        ['| Vertical', () => addConstraint({ id: uid('c'), type: 'axis', a: it.id, axis: 'v' }, it.id)],
      ]));
    }
    if (!constraintsOf(it.id).some(c => c.type === 'fix')) {
      panel.appendChild(btnRow([['📌 Fix in place', () =>
        addConstraint({ id: uid('c'), type: 'fix', a: it.id, x1: it.x1, y1: it.y1, x2: it.x2, y2: it.y2 }, it.id)]]));
    }
    panel.appendChild(propSelect('Thickness', it.th, [['3.5', '3½″ (2×4)'], ['4.5', '4½″ (2×4+drywall)'], ['6', '6″'], ['8', '8″']], nv => {
      pushUndo();
      // dist ties hold their clear span; rejected if the sketch can't absorb it
      if (!setWallThickness(state.doc, it, parseFloat(nv))) hooks.notify(CONFLICT_MSG);
      clampOpenings(state.doc); hooks.markDirty();
    }));
    panel.appendChild(btnRow([['Delete', deleteSel, 'danger']]));
  }
  else if (r.kind === 'openings') {
    panel.appendChild(h3(OPENING_DEFS[it.type as OpeningType]?.label || 'Opening'));
    panel.appendChild(propSelect('Type', it.type,
      (Object.keys(OPENING_DEFS) as OpeningType[]).map(k => [k, OPENING_DEFS[k].label] as [string, string]),
      nv => {
        pushUndo();
        it.type = nv;
        if (nv === 'door') { it.hinge = it.hinge || 0; it.swing = it.swing || 1; }
        else if (nv === 'double' || nv === 'bifold') { delete it.hinge; it.swing = it.swing || 1; }
        else if (nv === 'pocket') { it.hinge = it.hinge || 0; delete it.swing; }
        else { delete it.hinge; delete it.swing; }
        clampOpenings(state.doc); hooks.markDirty(); renderPanel();
      }));
    panel.appendChild(propLen('Width', it.width, nv => {
      if (nv > 1) { pushUndo(); it.width = nv; clampOpenings(state.doc); hooks.markDirty(); }
    }));
    panel.appendChild(propLen('From wall start', it.t - it.width / 2, nv => {
      pushUndo();
      const wl = state.doc.walls.find(w => w.id === it.wallId);
      const len = wl ? wallVec(wl).len : 1e9;
      it.t = clamp(nv + it.width / 2, it.width / 2, len - it.width / 2);
      hooks.markDirty();
    }));
    if (it.type === 'door') {
      panel.appendChild(btnRow([['Flip hinge (H)', () => flipDoor('hinge')], ['Flip swing (S)', () => flipDoor('swing')]]));
    }
    if (it.type === 'double' || it.type === 'bifold') {
      panel.appendChild(btnRow([['Flip swing (S)', () => flipDoor('swing')]]));
    }
    if (it.type === 'pocket') {
      panel.appendChild(btnRow([['Flip side (H)', () => flipDoor('hinge')]]));
    }
    panel.appendChild(btnRow([['Delete', deleteSel, 'danger']]));
  }
  else if (r.kind === 'fixtures') {
    const def = FIXTURES[it.type] || FIXTURES.box;
    panel.appendChild(h3(def.label));
    panel.appendChild(propLen('Width', it.w, nv => { if (nv > 0) { pushUndo(); it.w = nv; hooks.markDirty(); } }));
    panel.appendChild(propLen('Depth', it.h, nv => { if (nv > 0) { pushUndo(); it.h = nv; hooks.markDirty(); } }));
    panel.appendChild(propNum('Rotation°', it.rot || 0, nv => { pushUndo(); it.rot = ((nv % 360) + 360) % 360; hooks.markDirty(); }));
    if (it.type === 'shelves')
      panel.appendChild(propNum('Shelves', it.shelves || 4, nv => {
        pushUndo(); it.shelves = Math.max(1, Math.min(12, Math.round(nv))); hooks.markDirty();
      }));
    panel.appendChild(propText('Label', it.label || '', nv => { pushUndo(); it.label = nv; hooks.markDirty(); }));
    panel.appendChild(btnRow([['Rotate 90° (R)', () => rotateSel(90)], ['Duplicate (⌘D)', duplicateSel]]));
    panel.appendChild(btnRow([['Delete', deleteSel, 'danger']]));
  }
  else if (r.kind === 'labels') {
    panel.appendChild(h3('Text'));
    panel.appendChild(propText('Text', it.text, nv => { if (nv) { pushUndo(); it.text = nv; hooks.markDirty(); } }));
    panel.appendChild(propLen('Size', it.size || 8, nv => { if (nv > 0) { pushUndo(); it.size = nv; hooks.markDirty(); } }));
    panel.appendChild(btnRow([['Delete', deleteSel, 'danger']]));
  }
  requestDraw();
}

/* ---- Constrain tool (Fusion-style): arm a relationship, click walls ---- */

const CONSTRAIN_TYPES: { key: string; label: string; solo?: boolean; hint: string }[] = [
  { key: 'perp', label: '⊥ Perpendicular', hint: 'The second wall squares to the first' },
  { key: 'par', label: '∥ Parallel', hint: 'The second wall aligns to the first' },
  { key: 'angle', label: '∠ Lock angle', hint: 'Keeps the current angle between them' },
  { key: 'dist', label: '🔗 Lock distance', hint: 'Keeps the current clear gap (near-parallel walls)' },
  { key: 'equal', label: '= Equal length', hint: 'The second wall matches the first' },
  { key: 'colin', label: '≡ Collinear', hint: 'Both walls on one straight line' },
  { key: 'coin', label: '⚯ End onto wall', hint: 'The first wall’s near end sticks to the second wall' },
  { key: 'mid', label: '⋈ End at midpoint', hint: 'The first wall’s near end sits at the second wall’s middle' },
  { key: 'h', label: '— Horizontal', solo: true, hint: 'One click' },
  { key: 'v', label: '| Vertical', solo: true, hint: 'One click' },
  { key: 'fix', label: '📌 Fix in place', solo: true, hint: 'One click — the wall can never move' },
];

function renderConstrain(panel: HTMLElement): void {
  panel.appendChild(h3('Constrain'));
  const armed = CONSTRAIN_TYPES.find(t => t.key === state.constrainType) || CONSTRAIN_TYPES[0];
  const tip = document.createElement('div');
  tip.style.cssText = 'font-size:12px;opacity:.75;margin:2px 0 10px;line-height:1.4';
  tip.textContent = armed.solo
    ? 'Click a wall to apply it.'
    : state.constrainFirst
      ? 'Now click the second wall — the first one stays put.'
      : 'Click the first wall (it stays put), then the second.';
  panel.appendChild(tip);
  for (const t of CONSTRAIN_TYPES) {
    const b = document.createElement('button');
    b.className = 'doortype' + (t.key === state.constrainType ? ' active' : '');
    b.textContent = t.label;
    b.title = t.hint;
    b.dataset.ctype = t.key;
    b.addEventListener('click', () => {
      state.constrainType = t.key;
      state.constrainFirst = null; state.sel.clear();
      renderPanel(); requestDraw();
    });
    panel.appendChild(b);
  }
}

/** Canvas click while the Constrain tool is active. */
export function applyConstrainClick(wl: Wall, p: { x: number; y: number }): void {
  const t = state.constrainType;
  const cs = state.doc.constraints || [];
  if (t === 'h' || t === 'v' || t === 'fix') {
    const has = t === 'fix'
      ? cs.some(c => c.type === 'fix' && c.a === wl.id)
      : cs.some(c => c.type === 'axis' && c.a === wl.id && c.axis === t);
    if (!has)
      addConstraint(t === 'fix'
        ? { id: uid('c'), type: 'fix', a: wl.id, x1: wl.x1, y1: wl.y1, x2: wl.x2, y2: wl.y2 }
        : { id: uid('c'), type: 'axis', a: wl.id, axis: t }, wl.id);
    state.sel = new Set([wl.id]);
    renderPanel(); return;
  }
  const first = state.constrainFirst;
  if (!first || first.id === wl.id) {
    state.constrainFirst = { id: wl.id, x: p.x, y: p.y };
    state.sel = new Set([wl.id]);
    renderPanel(); return;
  }
  const A = state.doc.walls.find(w => w.id === first.id);
  const B = wl;
  const done = () => { state.constrainFirst = null; renderPanel(); };
  if (!A) return done();
  state.sel = new Set([A.id, B.id]);
  const pairHas = (...types: string[]) => cs.some(c => types.includes(c.type) && 'b' in c &&
    ((c.a === A.id && c.b === B.id) || (c.a === B.id && c.b === A.id)));
  if (t === 'perp' || t === 'par' || t === 'angle') {
    if (pairHas('angle', 'colin')) { hooks.notify('These walls already have an angle relationship.'); return done(); }
    const deg = t === 'perp' ? 90 : t === 'par' ? 0 : Math.round(wallAngleDeg(A, B) * 2) / 2;
    addConstraint({ id: uid('c'), type: 'angle', a: A.id, b: B.id, deg }, A.id);
  } else if (t === 'dist') {
    const vA = wallVec(A), vB = wallVec(B);
    if (Math.abs(vA.ux * vB.uy - vA.uy * vB.ux) >= .09) {
      hooks.notify('Lock distance needs near-parallel walls — make them parallel first.'); return done();
    }
    if (pairHas('dist')) { hooks.notify('These walls already have a distance lock.'); return done(); }
    addConstraint({ id: uid('c'), type: 'dist', a: A.id, b: B.id, dist: constraintDist(A, B) }, A.id);
  } else if (t === 'equal') {
    if (pairHas('equal')) { hooks.notify('These walls are already equal.'); return done(); }
    addConstraint({ id: uid('c'), type: 'equal', a: A.id, b: B.id }, A.id);
  } else if (t === 'colin') {
    if (pairHas('colin', 'angle')) { hooks.notify('These walls already have an angle relationship.'); return done(); }
    addConstraint({ id: uid('c'), type: 'colin', a: A.id, b: B.id }, A.id);
  } else if (t === 'coin' || t === 'mid') {
    // the end of the FIRST wall nearest its click point gets tied; host stays
    const end: 0 | 1 =
      Math.hypot(first.x - A.x1, first.y - A.y1) <= Math.hypot(first.x - A.x2, first.y - A.y2) ? 0 : 1;
    if (cs.some(c => (c.type === 'coin' || c.type === 'mid') && c.a === A.id && c.end === end)) {
      hooks.notify('That end is already tied to a wall.'); return done();
    }
    addConstraint(t === 'coin'
      ? { id: uid('c'), type: 'coin', a: A.id, end, b: B.id }
      : { id: uid('c'), type: 'mid', a: A.id, end, b: B.id }, B.id);
  }
  done();
}

function addConstraint(c: Constraint, pinId?: string): void {
  pushUndo();
  (state.doc.constraints ||= []).push(c);
  const seeds = 'b' in c ? [c.a, c.b] : [c.a];
  let ok = solveConstraints(state.doc, new Set(pinId ? [pinId] : []), seeds).ok;
  if (!ok && 'b' in c && pinId) {
    // the preferred datum can't hold (its partner is axis-locked/grounded):
    // swap roles so the freer wall does the moving — Fusion moves whichever can
    ok = solveConstraints(state.doc, new Set([pinId === c.a ? c.b : c.a]), seeds).ok;
  }
  if (!ok) {
    // Fusion refuses a constraint the sketch can't satisfy — so do we
    state.doc.constraints = state.doc.constraints!.filter(x => x !== c);
    hooks.notify("Those walls can't satisfy that constraint together, so it wasn't added.");
  }
  clampOpenings(state.doc);
  hooks.markDirty(); renderPanel();
}

/** Constraint section shown when exactly two walls are selected. */
function renderPairSection(panel: HTMLElement, A: Wall, B: Wall): void {
  const between = (state.doc.constraints || []).filter(c =>
    'b' in c && ((c.a === A.id && c.b === B.id) || (c.a === B.id && c.b === A.id)));
  for (const c of between) {
    const row = constraintRow(c, A);
    if (row) panel.appendChild(row);
  }
  const vA = wallVec(A), vB = wallVec(B);
  const cross = Math.abs(vA.ux * vB.uy - vA.uy * vB.ux);
  const create: [string, () => void][] = [];
  if (!between.some(c => c.type === 'angle')) {
    // pin A (the first-selected wall): it stays put, B squares/aligns to it
    create.push(['⊥ Perpendicular', () => addConstraint({ id: uid('c'), type: 'angle', a: A.id, b: B.id, deg: 90 }, A.id)]);
    create.push(['∥ Parallel', () => addConstraint({ id: uid('c'), type: 'angle', a: A.id, b: B.id, deg: 0 }, A.id)]);
    const cur = Math.round(wallAngleDeg(A, B) * 2) / 2;
    if (cur !== 0 && cur !== 90)
      create.push([`∠ Lock angle (${cur}°)`, () => addConstraint({ id: uid('c'), type: 'angle', a: A.id, b: B.id, deg: cur }, A.id)]);
  }
  if (!between.some(c => c.type === 'dist') && cross < .09) {   // near-parallel
    const s = constraintDist(A, B);
    const clearD = Math.abs(s) - A.th / 2 - B.th / 2;
    create.push([`🔗 Lock distance (${fmtLen(clearD)} clear)`, () =>
      addConstraint({ id: uid('c'), type: 'dist', a: A.id, b: B.id, dist: s }, A.id)]);
  }
  if (!between.some(c => c.type === 'equal'))
    create.push(['= Equal length', () => addConstraint({ id: uid('c'), type: 'equal', a: A.id, b: B.id }, A.id)]);
  if (!between.some(c => c.type === 'colin' || c.type === 'angle'))
    create.push(['≡ Collinear', () => addConstraint({ id: uid('c'), type: 'colin', a: A.id, b: B.id }, A.id)]);
  if (!between.some(c => c.type === 'coin')) {
    // an end of one wall hovering near the other's span (not its corners) can
    // be tied into a T-junction — the host stays put, the end snaps onto it
    outer: for (const [P, Q] of [[A, B], [B, A]] as const) {
      const vq = wallVec(Q);
      for (const end of [0, 1] as const) {
        const ex = end ? P.x2 : P.x1, ey = end ? P.y2 : P.y1;
        const r = distToSeg(ex, ey, Q.x1, Q.y1, Q.x2, Q.y2);
        if (r.d < 12 && r.t > 2 && r.t < vq.len - 2) {
          create.push(['⚯ Attach end to wall', () =>
            addConstraint({ id: uid('c'), type: 'coin', a: P.id, end, b: Q.id }, Q.id)]);
          break outer;
        }
      }
    }
  }
  for (const [txt, fn] of create) panel.appendChild(btnRow([[txt, fn]]));
}

export function h3(t: string): HTMLElement { const e = document.createElement('h3'); e.textContent = t; return e; }
function propRow(label: string, input: HTMLElement): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop';
  const l = document.createElement('label'); l.textContent = label;
  row.appendChild(l); row.appendChild(input);
  return row;
}
function propLen(label: string, val: number, cb: (nv: number) => void): HTMLElement {
  const i = document.createElement('input'); i.type = 'text';
  i.value = plain(val);
  const commit = () => {
    const nv = parseLen(i.value, 'in');
    if (nv !== null) cb(nv);
    renderPanel();
  };
  i.addEventListener('keydown', e => { if (e.key === 'Enter') i.blur(); e.stopPropagation(); });
  i.addEventListener('blur', commit);
  return propRow(label, i);
}
function propNum(label: string, val: number, cb: (nv: number) => void, step = 15): HTMLElement {
  const i = document.createElement('input'); i.type = 'number'; i.step = String(step); i.value = String(val);
  i.addEventListener('keydown', e => { if (e.key === 'Enter') i.blur(); e.stopPropagation(); });
  i.addEventListener('change', () => { const v = parseFloat(i.value); if (!isNaN(v)) cb(v); renderPanel(); });
  return propRow(label, i);
}
function propText(label: string, val: string, cb: (nv: string) => void): HTMLElement {
  const i = document.createElement('input'); i.type = 'text'; i.value = val; i.style.textAlign = 'left';
  i.addEventListener('keydown', e => { if (e.key === 'Enter') i.blur(); e.stopPropagation(); });
  i.addEventListener('blur', () => cb(i.value.trim()));
  return propRow(label, i);
}
function propSelect(label: string, val: number | string, opts: [string, string][], cb: (nv: string) => void): HTMLElement {
  const s = document.createElement('select');
  for (const [v, txt] of opts) { const o = document.createElement('option'); o.value = v; o.textContent = txt; s.appendChild(o); }
  s.value = String(val);
  if (s.selectedIndex < 0) { const o = document.createElement('option'); o.value = String(val); o.textContent = fmtLen(Number(val)); s.appendChild(o); s.value = String(val); }
  s.addEventListener('change', () => cb(s.value));
  return propRow(label, s);
}

/** Panel row for one constraint, seen from wall `ref`. */
function constraintRow(c: Constraint, ref: Wall): HTMLElement | null {
  let row: HTMLElement;
  if (c.type === 'dist') {
    const other = state.doc.walls.find(w => w.id === (c.a === ref.id ? c.b : c.a));
    if (!other) return null;
    const clearD = Math.abs(c.dist) - ref.th / 2 - other.th / 2;
    row = propLen('🔗 Distance', clearD, nv => {
      if (nv > 0) {
        pushUndo();
        const prev = c.dist;
        c.dist = (c.dist < 0 ? -1 : 1) * (nv + ref.th / 2 + other.th / 2);
        if (!solveConstraints(state.doc, new Set([ref.id]), [c.a, c.b]).ok) {
          c.dist = prev; hooks.notify(CONFLICT_MSG);
        }
        clampOpenings(state.doc); hooks.markDirty();
      }
    });
    row.querySelector('label')!.setAttribute('title', 'Clear distance to the paired wall — held while dragging');
  } else if (c.type === 'angle') {
    const label = c.deg === 90 ? '⊥ Angle°' : c.deg === 0 ? '∥ Angle°' : '∠ Angle°';
    row = propNum(label, c.deg, nv => {
      pushUndo();
      const prev = c.deg;
      (c as AngleConstraint).deg = ((nv % 180) + 180) % 180;
      // the selected wall stays put; its partner rotates to the new angle
      if (!solveConstraints(state.doc, new Set([ref.id]), [c.a, (c as AngleConstraint).b]).ok) {
        (c as AngleConstraint).deg = prev; hooks.notify(CONFLICT_MSG);
      }
      clampOpenings(state.doc); hooks.markDirty();
    }, 45);
    row.querySelector('label')!.setAttribute('title', 'Angle to the paired wall (90 = perpendicular, 0 = parallel)');
  } else if (c.type === 'axis') {
    row = propRow(c.axis === 'h' ? '— Horizontal' : '| Vertical', document.createElement('span'));
  } else if (c.type === 'coin') {
    row = propRow(c.a === ref.id ? '⚯ End tied to wall' : '⚯ Wall tied onto this', document.createElement('span'));
    row.querySelector('label')!.setAttribute('title',
      'T-junction: the endpoint stays on the other wall (slides along it, follows it around)');
  } else if (c.type === 'mid') {
    row = propRow(c.a === ref.id ? '⋈ End at wall midpoint' : '⋈ Wall tied to midpoint', document.createElement('span'));
    row.querySelector('label')!.setAttribute('title',
      'The endpoint sits exactly at the middle of the other wall');
  } else if (c.type === 'colin') {
    row = propRow('≡ Collinear', document.createElement('span'));
    row.querySelector('label')!.setAttribute('title', 'Both walls stay on one straight line');
  } else if (c.type === 'fix') {
    row = propRow('📌 Fixed in place', document.createElement('span'));
    row.querySelector('label')!.setAttribute('title', 'Grounded: this wall can never move — remove to free it');
  } else { // equal
    row = propRow('= Equal length', document.createElement('span'));
  }
  const del = document.createElement('button');
  del.className = 'lockbtn on'; del.textContent = '✕'; del.title = 'Remove constraint';
  del.addEventListener('click', () => removeConstraint(c));
  row.appendChild(del);
  return row;
}

function btnRow(defs: [string, () => void, string?][]): HTMLElement {
  const row = document.createElement('div'); row.className = 'btnrow';
  for (const [txt, fn, cls] of defs) {
    const b = document.createElement('button'); b.textContent = txt; if (cls) b.className = cls;
    b.addEventListener('click', fn);
    row.appendChild(b);
  }
  return row;
}

/** Big "what next" buttons that arm a tool. rows = [tool id, title, subtitle]. */
function actionList(rows: [string, string, string][]): HTMLElement {
  const acts = document.createElement('div'); acts.className = 'actions';
  for (const [id, title, sub] of rows) {
    const t = TOOLS.find(x => x.id === id)!;
    const b = document.createElement('button'); b.className = 'action'; b.dataset.tool = id;
    b.innerHTML = `<svg viewBox="0 0 16 16">${t.icon}</svg><span><b>${title}</b><small>${sub}</small></span><kbd>${t.key}</kbd>`;
    b.addEventListener('click', () => hooks.setTool(id));
    acts.appendChild(b);
  }
  return acts;
}

/** Four walls forming a rectangle: edit it as a room (inside size), then what's next. */
function renderRoomSel(panel: HTMLElement, r: RoomSel): void {
  panel.appendChild(h3('Room'));
  const ins = roomInside(r);
  const sum = document.createElement('div'); sum.className = 'tips'; sum.id = 'roomSummary';
  sum.innerHTML = `<div><b>${fmtLen(ins.w)} × ${fmtLen(ins.h)}</b> inside · ${fmtArea(ins.w * ins.h)}</div>`;
  panel.appendChild(sum);
  const lockedW = r.top.locked || r.bottom.locked, lockedH = r.left.locked || r.right.locked;
  const wRow = propLen('Width inside', ins.w, nv => { if (nv > 0) { pushUndo(); resizeRect(state.doc, r, nv, null); hooks.markDirty(); } });
  const hRow = propLen('Depth inside', ins.h, nv => { if (nv > 0) { pushUndo(); resizeRect(state.doc, r, null, nv); hooks.markDirty(); } });
  if (lockedW) (wRow.querySelector('input') as HTMLInputElement).disabled = true;
  if (lockedH) (hRow.querySelector('input') as HTMLInputElement).disabled = true;
  wRow.querySelector('label')!.title = hRow.querySelector('label')!.title = 'Face to face — the top-left corner stays put';
  panel.appendChild(wRow); panel.appendChild(hRow);
  // one thickness for all four walls (exterior walls are usually 6″); centerlines stay, so the inside shrinks/grows
  const ths = [r.top, r.right, r.bottom, r.left].map(w => w.th);
  const thVal = ths.every(t => t === ths[0]) ? String(ths[0]) : 'mixed';
  const thRow = propSelect('Walls', thVal, [['3.5', '3½″ (2×4)'], ['4.5', '4½″ (2×4+drywall)'], ['6', '6″ (exterior)'], ['8', '8″'], ...(thVal === 'mixed' ? [['mixed', 'mixed'] as [string, string]] : [])], nv => {
    if (nv === 'mixed') return;
    pushUndo();
    for (const w of [r.top, r.right, r.bottom, r.left]) setWallThickness(state.doc, w, parseFloat(nv));
    clampOpenings(state.doc); hooks.markDirty(); renderPanel();
  });
  thRow.id = 'roomWalls';
  panel.appendChild(thRow);
  panel.appendChild(h3('Next'));
  panel.appendChild(actionList([
    ['door', 'Add a door', 'click a wall'],
    ['window', 'Add a window', 'click a wall'],
    ['fixture', 'Add furniture', 'beds, sofas, sinks…'],
    ['label', 'Name the room', 'click inside it'],
  ]));
  panel.appendChild(btnRow([['Delete', deleteSel, 'danger'], ['Duplicate', duplicateSel]]));
}

/** Nothing selected: big buttons for what people do next, then the reference. */
function renderTips(panel: HTMLElement): void {
  panel.appendChild(h3(state.doc.walls.length ? 'Add to the plan' : 'Start your plan'));
  panel.appendChild(actionList([
    ['room', 'Draw a room', 'drag a rectangle'],
    ['wall', 'Draw walls', 'corner to corner'],
    ['door', 'Add a door', 'click a wall'],
    ['window', 'Add a window', 'click a wall'],
    ['fixture', 'Add furniture', 'beds, sofas, sinks…'],
    ['label', 'Add text', 'name a room'],
  ]));
  const det = document.createElement('details'); det.className = 'tipsbox';
  det.innerHTML = '<summary>Tips &amp; shortcuts</summary>';
  const tips = document.createElement('div'); tips.className = 'tips';
  tips.innerHTML = `
    <div><b>Draw walls</b> — press <kbd>W</kbd>, click corner to corner. Type a length like <kbd>12'6</kbd> and hit <kbd>Enter</kbd> for exact walls.</div>
    <div><b>Openings</b> — <kbd>D</kbd> door (pick swing / double / bifold / sliding / pocket / cased in the panel), <kbd>N</kbd> window, then click a wall. <kbd>H</kbd>/<kbd>S</kbd> flip hinge &amp; swing.</div>
    <div><b>Fixtures</b> — <kbd>X</kbd> opens the catalog (grouped by room, searchable). <kbd>R</kbd> rotates.</div>
    <div><b>Constraints</b> — select two walls (⇧click) for ⊥ / ∥ / ∠ / 🔗 distance / = equal; click any dimension or badge on the canvas to edit it.</div>
    <div><b>Move around</b> — scroll to pan, pinch or <kbd>⌘</kbd>-scroll to zoom, <kbd>Space</kbd>-drag to pan, <kbd>F</kbd> fits everything.</div>
    <div><b>Measure</b> — <kbd>M</kbd>, then drag between any two points.</div>
    <div>Everything autosaves as you work — on this device, or in your account once you sign in (then it syncs live to the 3D/VR view and share links).</div>`;
  det.appendChild(tips);
  panel.appendChild(det);
}

/** Room tool: how to drag, or type the inside size and place it. */
function renderRoomPanel(panel: HTMLElement): void {
  panel.appendChild(h3('Room'));
  const tip = document.createElement('div'); tip.className = 'tips';
  tip.innerHTML = '<div>Drag on the canvas to draw a room. <kbd>⇧</kbd> keeps it square; corners snap to walls you already have.</div>';
  panel.appendChild(tip);
  panel.appendChild(h3('Or type the inside size'));
  const roomSize = roomSizes[getUnits()];
  panel.appendChild(propLen('Width', roomSize.w, nv => { if (nv > 0) roomSize.w = nv; }));
  panel.appendChild(propLen('Depth', roomSize.h, nv => { if (nv > 0) roomSize.h = nv; }));
  const row = btnRow([['Place room', () => {
    // centered in the visible canvas, on whole inches; selected so it can be dragged into place
    const cx = state.view.x + CSS_W / 2 / state.view.zoom, cy = state.view.y + CSS_H / 2 / state.view.zoom;
    addRoom(rectFromClear(cx, cy, roomSize.w, roomSize.h));
  }]]);
  (row.firstChild as HTMLElement).classList.add('primary');
  row.id = 'placeRoomRow';
  panel.appendChild(row);
}

/** Type picker shown while the Door tool is active (rail keeps one Door
    button; the family lives here). */
function renderDoorTypes(panel: HTMLElement): void {
  panel.appendChild(h3('Door type'));
  const wrap = document.createElement('div'); wrap.className = 'doortypes';
  for (const id of DOOR_TYPES) {
    const def = OPENING_DEFS[id];
    const b = document.createElement('button');
    b.className = 'doortype' + (state.tool === id ? ' active' : '');
    b.innerHTML = `<span>${def.label}</span><kbd>${def.key}</kbd>`;
    b.addEventListener('click', () => hooks.setTool(id));
    wrap.appendChild(b);
  }
  panel.appendChild(wrap);
  const tip = document.createElement('div'); tip.className = 'tips';
  tip.innerHTML = '<div>Click a wall to place. <kbd>H</kbd> flips hinge/side, <kbd>S</kbd> flips swing.</div>';
  panel.appendChild(tip);
}

function renderPalette(panel: HTMLElement): void {
  panel.appendChild(h3('Fixtures'));
  // search bar — filters by name, category, and synonyms; query survives re-renders
  const sb = document.createElement('input');
  sb.type = 'search'; sb.className = 'palsearch'; sb.placeholder = 'Search fixtures…';
  sb.value = state.palQuery;
  panel.appendChild(sb);
  const body = document.createElement('div');
  body.style.display = 'flex'; body.style.flexDirection = 'column'; body.style.gap = '10px';
  panel.appendChild(body);
  const build = () => {
    body.innerHTML = '';
    const q = state.palQuery.trim().toLowerCase();
    let any = false;
    for (const cat of FIXTURE_CATEGORIES) {
      const keys = cat.items.filter(k => {
        if (!q) return true;
        const hay = `${k} ${FIXTURES[k].label} ${cat.name} ${FIXTURE_KW[k] || ''}`.toLowerCase();
        return q.split(/\s+/).every(term => hay.includes(term));
      });
      if (!keys.length) continue;
      any = true;
      const hd = document.createElement('div'); hd.className = 'palcat'; hd.textContent = cat.name;
      body.appendChild(hd);
      const grid = document.createElement('div'); grid.className = 'palette';
      for (const key of keys) grid.appendChild(palItem(key));
      body.appendChild(grid);
    }
    if (!any) {
      const none = document.createElement('div'); none.className = 'tips';
      none.textContent = 'No matches — try “sink”, “closet”, “couch”…';
      body.appendChild(none);
    }
  };
  sb.addEventListener('input', () => { state.palQuery = sb.value; build(); });
  sb.addEventListener('keydown', e => {
    e.stopPropagation();
    if (e.key === 'Escape') { sb.value = ''; state.palQuery = ''; build(); }
  });
  build();
}

function palItem(key: string): HTMLElement {
  const C = palette();
  const def = FIXTURES[key];
  const b = document.createElement('div');
  b.className = 'pal' + (state.fixtureType === key ? ' active' : '');
  b.dataset.key = key;
  b.title = 'Drag onto the plan, or click then click where it goes';
  b.addEventListener('pointerdown', e => beginPaletteDrag(key, e));
  const cv = document.createElement('canvas');
  cv.width = 144; cv.height = 88;
  const g = cv.getContext('2d')!;
  const sc = Math.min(120 / def.w, 68 / def.h);
  g.translate(72, 44); g.scale(sc, sc);
  g.strokeStyle = C.ink; g.lineWidth = 1.4 / sc; g.lineCap = 'round'; g.lineJoin = 'round';
  def.draw(g, def.w, def.h);
  const sp = document.createElement('span');
  sp.textContent = `${def.label} · ${Math.round(def.w / 12 * 10) / 10}×${Math.round(def.h / 12 * 10) / 10}′`;
  b.appendChild(cv); b.appendChild(sp);
  b.addEventListener('click', () => {
    state.fixtureType = key;
    const g2 = state.placeGhost || { rot: 0 };
    state.placeGhost = { w: def.w, h: def.h, rot: g2.rot || 0, x: state.lastMouse.x, y: state.lastMouse.y };
    renderPanel(); requestDraw();
  });
  return b;
}
